import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { FolderIcon, EyeIcon, UserGroupIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const GET_CURRENT_USER_FOR_PARTIAL_SALES = gql`
  query GetCurrentUserForPartialSales {
    getCurrentUser {
      id
      features
      company { feature_types }
    }
  }
`;

const GET_OPEN_PARTIAL_SALES = gql`
  query GetOpenPartialSales($limit: Int) {
    myPartialSales: getOpenPartialSales(limit: $limit) {
      id
      sale_status
      views_count
      interests_count
      user { id }
    }
  }
`;

const hasFeature = (cu, key) => {
  const userHas = Array.isArray(cu?.features) && cu.features.includes(key);
  const companyHas = Array.isArray(cu?.company?.feature_types) && cu.company.feature_types.includes(key);
  return userHas || companyHas;
};

const numberFormat = (n) => new Intl.NumberFormat('en-US').format(Number(n || 0));

const MyPartialSaleMetricsCard = () => {
  // Fetch current user first (from cache if available)
  const { data: cuData, loading: cuLoading } = useQuery(GET_CURRENT_USER_FOR_PARTIAL_SALES, {
    fetchPolicy: 'cache-first'
  });
  const cu = cuData?.getCurrentUser;

  // Then fetch open partial sales once we have a user id
  const { data: listingsData, loading: listingsLoading } = useQuery(GET_OPEN_PARTIAL_SALES, {
    variables: { limit: 50 },
    skip: !cu?.id,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  if (cuLoading || (!cu?.id)) return (
    <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full border border-succession-border"><div className="px-4 py-5 sm:p-6 text-sm text-succession-text-secondary">Loading…</div></div>
  );

  if (cu && !hasFeature(cu, 'partial_sales')) return null;

  const safeListings = Array.isArray(listingsData?.myPartialSales) ? listingsData.myPartialSales : [];
  const listings = safeListings.filter((l) => String(l?.user?.id) === String(cu?.id));
  const totalListings = listings.length;
  const published = listings.filter((l) => String(l.sale_status) === 'open').length;
  const drafts = listings.filter((l) => String(l.sale_status) === 'draft').length;
  const totalViews = listings.reduce((acc, l) => acc + Number(l.views_count || 0), 0);
  const totalInterests = listings.reduce((acc, l) => acc + Number(l.interests_count || 0), 0);

  const hasAny = totalListings > 0;

  if (listingsLoading && !hasAny) {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full border border-succession-border"><div className="px-4 py-5 sm:p-6 text-sm text-succession-text-secondary">Loading…</div></div>
    );
  }

  return (
    <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full border border-succession-border">
      <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
        {hasAny && (
          <>
            <h3>My Partial Sales</h3>
            <h4 className="text-sm text-succession-text-secondary">Performance of your listings</h4>
          </>
        )}

        {hasAny ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-succession-text-secondary">Total Listings</div>
                <div className="text-lg font-semibold text-succession-text-primary">{numberFormat(totalListings)}</div>
              </div>
              <div className="w-9 h-9 rounded-md bg-succession-bg-primary border border-succession-border flex items-center justify-center">
                <FolderIcon className="w-5 h-5 text-succession-primary" />
              </div>
            </div>
            <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-succession-text-secondary">Total Views</div>
                <div className="text-lg font-semibold text-succession-text-primary">{numberFormat(totalViews)}</div>
              </div>
              <div className="w-9 h-9 rounded-md bg-succession-bg-primary border border-succession-border flex items-center justify-center">
                <EyeIcon className="w-5 h-5 text-succession-primary" />
              </div>
            </div>
            <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-succession-text-secondary">Total Interests</div>
                <div className="text-lg font-semibold text-succession-text-primary">{numberFormat(totalInterests)}</div>
              </div>
              <div className="w-9 h-9 rounded-md bg-succession-bg-primary border border-succession-border flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 text-succession-primary" />
              </div>
            </div>
            <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-succession-text-secondary">Published / Drafts</div>
                <div className="text-lg font-semibold text-succession-text-primary">{numberFormat(published)} / {numberFormat(drafts)}</div>
              </div>
              <div className="w-9 h-9 rounded-md bg-succession-bg-primary border border-succession-border flex items-center justify-center">
                <ClipboardDocumentListIcon className="w-5 h-5 text-succession-primary" />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex-1 flex flex-col items-center justify-center text-center">
            <h3 className="mb-2">My Partial Sales</h3>
            <div className="text-succession-text-secondary text-sm max-w-md">
              Selling a portion of your book could be the key to unlocking greater flexibility, efficiency, and long-term success.
            </div>
            <a
              href="/succession/my_partial_sales"
              className="mt-4 inline-flex items-center px-4 py-2 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary"
            >
              Start a Partial Sale
            </a>
          </div>
        )}

        {hasAny && (
          <div className="mt-auto pt-2 flex justify-center">
            <a href="/succession/my_partial_sales" className="text-sm text-succession-primary hover:text-succession-primary-hover">
              View My Partial Sales &gt;
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPartialSaleMetricsCard;


